import { useCallback, useEffect, useState } from 'react'
import { Grid, Header } from 'semantic-ui-react'
import moment from 'moment'
import { DateTime } from 'luxon'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'
import { useBooleanFlagValue } from '@openfeature/react-sdk'

import CurrencyFormatLabel from '../../shared/CurrencyFormatLabel'
import { centsToCurrency } from '../../../utils/currencyHelpers'
import {
  fetchSubscriptions,
  selectHeardProductSubscription,
  selectSubscription,
  SUBSCRIPTION_DEFAULT_KEY,
} from '../../../reducers/subscription.slice'
import {
  fetchBillingPortalSession,
  submitCancellationRequest,
} from '../../../actions/settings/billingActions'
import {
  Button,
  Card,
  Table,
  Text,
  GridRowColumn,
  Label,
  Modal,
  Alert,
} from '../../BaseComponents'
import { DATE_FORMATS, DATE_FORMATS_LUXON } from '../../../utils/dateHelpers'
import {
  DeviceWidth,
  useIsDeviceWidth,
} from '../../../utils/deviceWidthHelpers'
import { ResponsiveHeader } from './ResponsiveHeader'
import {
  getCurrentUser,
  getFreeTrialEndsAt,
  getFreeTrialStatus,
  hasManualRequest,
  isCancelling,
  isFreeTrialPromoCode,
} from '../../../selectors/user.selectors'
import { useReselector } from '../../../utils/sharedHooks'
import {
  DOWNLOAD_ACCOUNT_ZIP_KEY,
  downloadAccountInformation,
  fetchCurrentUserDetailsIfNeeded,
  updateMembership,
} from '../../../actions/userActions'
import { selectCurrentAnnualTaxYear } from '../../../features/Admin/AnnualTaxDetails/annualTaxDetails.selector'
import {
  useAnalyticsTrack,
  useAnalyticsView,
} from '../../../features/Amplitude'
import { sendUserCancellationMessageZap } from '../../../actions/zapierActions'
import { fetchPayrollProfileIfNeeded } from '../../../features/Payroll/payrollActions'
import AccountDownloadModal from './AccountDownloadModal'
import { selectIsFetchingForKeys } from '../../../reducers/fetch'
import { useAppDispatch } from '../../../utils/typeHelpers'
import InvoiceList from './InvoiceList'
import { FEATURE_FLAG_KEYS } from '../../../features/OpenFeature'
import {
  CancellationStepIdentifier,
  createAutomaticCancellationAnalyticsRecord,
} from '../../../features/SelfServeCancellationFlow/userCancellation.slice'
import { useNavigate } from 'react-router-dom'
import { useGetSaveMoneyAndTimePageData } from '../../../features/SelfServeCancellationFlow/helpers'
import {
  fetchUserTransactions,
  USER_TRANSACTION_KEY,
} from '../../../features/Transactions/transactions.slice'
import {
  ALWAYS_FETCH_USER_TAX_ESTIMATES_KEY,
  fetchUserTaxEstimates,
} from '../../../features/Taxes/QuarterlyTaxEstimates/userTaxEstimates.slice'
import { ReactivatePlanModal } from './ReactivatePlanModal'
import {
  isEligibleForDiscount,
  selectCanSelfServeCancel,
  selectUserHasActiveAnnualCommitment,
} from '../../../features/SelfServeCancellationFlow/userCancellation.selectors'
import { TrialStatus } from '../../../reducers/auth/userReducer'
import { fetchAllAnnualTaxDetailsIfNeeded } from '../../../features/Admin/AnnualTaxDetails/annualTaxDetails.slice'
import { fetchAnnualTaxFilingsIfNeeded } from '../../../features/Taxes/AnnualTaxes/annualTaxFilings.slice'
import { fetchAnnualTaxFilingFormsIfNeeded } from '../../../features/Taxes/AnnualTaxes/annualTaxFilingForms.slice'
import { selectIsPayrollActive } from '../../../features/Payroll/payroll.selectors'
import { hasTaxfyleJobOrExtensionInProgress } from '../../../features/Taxes/AnnualTaxes/annualTaxFilings.selector'

const ConfirmManualCancellationModal = ({
  open,
  close,
}: {
  open: boolean
  close: () => void
}) => {
  const dispatch = useAppDispatch()
  const track = useAnalyticsTrack()
  const pageView = useAnalyticsView()

  const currentUser = useReselector(getCurrentUser)
  const subscription = useReselector(selectHeardProductSubscription)
  const [disabled, setDisabled] = useState(false)
  const [loading, setLoading] = useState(false)

  const [error, setError] = useState('')
  const currentTaxYear = useReselector(selectCurrentAnnualTaxYear)
  const hasTaxfyleJob = useReselector(
    hasTaxfyleJobOrExtensionInProgress,
    currentTaxYear
  )
  const hasActiveAnnualCommitment = useReselector(
    selectUserHasActiveAnnualCommitment
  )
  const isActiveGEPUser = useReselector(selectIsPayrollActive)
  useEffect(() => {
    pageView('confirm cancellation modal')
  }, [pageView])

  const cancelRequest = async () => {
    setDisabled(true)
    setLoading(true)
    const res = await submitCancellationRequest(currentUser?.id)(dispatch)
    track('submit manual cancellation request')
    if (res) {
      setDisabled(false)
      setLoading(false)

      await updateMembership(currentUser?.id, {
        manualCancellationRequestedAt: DateTime.now().toString(),
      })(dispatch)
      sendUserCancellationMessageZap(currentUser, 'manual_cancel')
      close()
      location.reload()
    } else {
      setError('We were unable to submit your cancellation request.')
      setDisabled(false)
      setLoading(false)
    }
  }

  const renderModalBody = () => {
    const endDate = subscription?.nextBillingDate
      ? DateTime.fromSeconds(subscription?.nextBillingDate).toFormat(
          DATE_FORMATS_LUXON.DISPLAY_SHORT
        )
      : 'the end of your billing period'
    if (hasTaxfyleJob) {
      return (
        <>
          <Text as="bodyLg">
            Since we have already started filing your {currentTaxYear} taxes, we
            cannot automatically cancel your subscription until your taxes are
            transmitted to the IRS. You have until {endDate} to get your taxes
            fully filed before your next renewal.
          </Text>
          <br />
          <Text as="bodyLg">
            If you want to continue, you can submit a request to cancel your
            subscription.
          </Text>
        </>
      )
    } else if (isActiveGEPUser) {
      return (
        <Text as="bodyLg">
          If you want to continue, you can submit a request to cancel your
          subscription.
        </Text>
      )
    } else if (hasActiveAnnualCommitment) {
      return (
        <>
          <Text as="bodyLg">
            Your contract includes an annual commitment to Heard.
          </Text>
          <Card type="subsection" backgroundColor="lightYellow">
            <b>What does this mean?</b>
            <ul>
              <li>Refunds are not available.</li>
              <li>You may owe the remaining balance of your subscription.</li>
            </ul>
          </Card>
          <Text as="bodyLg">
            If you want to continue, you can submit a request to cancel your
            subscription.
          </Text>
        </>
      )
    }
    return null
  }

  const renderActions = () => {
    return (
      <Modal.Actions>
        <Button
          variant="secondary"
          onClick={() => {
            close()
          }}
        >
          Close
        </Button>
        <Button
          variant="primary"
          onClick={cancelRequest}
          disabled={disabled}
          loading={loading}
        >
          Submit Request
        </Button>
      </Modal.Actions>
    )
  }

  return (
    <Modal
      size={'small'}
      dimmer={'inverted'}
      className="ConfirmCancellationModal"
      open={open}
      onClose={close}
    >
      <Modal.Header>
        <Header as="h2">
          Please note: We cannot process your cancellation automatically
        </Header>
      </Modal.Header>
      <Modal.Content>
        {error && <Alert type="error">{error}</Alert>}
        {renderModalBody()}
      </Modal.Content>
      <Modal.Actions>{renderActions()}</Modal.Actions>
    </Modal>
  )
}

const PlansAndBillingPanel = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const track = useAnalyticsTrack()
  const pageView = useAnalyticsView()
  const subscription = useReselector(selectSubscription)
  const primarySubscription = useReselector(selectHeardProductSubscription)
  const membershipIsCancelling = useReselector(isCancelling)
  const membershipHasManualRequest = useReselector(hasManualRequest)
  const currentUser = useReselector(getCurrentUser)
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const [reactivateOpen, setReactivateOpen] = useState(false)
  const [cancelPlanModalOpen, setCancelPlanModalOpen] = useState(false)
  const [downloadModalOpen, setDownloadModalOpen] = useState(false)
  const userFreeTrialStatus = useReselector(getFreeTrialStatus)
  const hasFreeTrialPromoCode = useReselector(isFreeTrialPromoCode)

  const downloadingFiles = useReselector(selectIsFetchingForKeys, [
    DOWNLOAD_ACCOUNT_ZIP_KEY,
  ])

  const isLoadingTransactionsAndUserTaxEstimates = useReselector(
    selectIsFetchingForKeys,
    [USER_TRANSACTION_KEY, ALWAYS_FETCH_USER_TAX_ESTIMATES_KEY]
  )

  const data = useGetSaveMoneyAndTimePageData()

  const enableDiscounts = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.enableCancellationDiscounts,
    false
  )

  const currentTaxYear = useReselector(selectCurrentAnnualTaxYear)

  const canSelfServeCancel = useReselector(
    selectCanSelfServeCancel,
    currentTaxYear
  )

  const eligibleForDiscount = useReselector(
    isEligibleForDiscount,
    enableDiscounts
  )
  const isInActiveFreeTrial =
    hasFreeTrialPromoCode && userFreeTrialStatus === TrialStatus.active
  const trialEndsAt = useReselector(getFreeTrialEndsAt)

  const saveFlowCancellationAction = useCallback(async () => {
    const record = await createAutomaticCancellationAnalyticsRecord({
      finalStepReached: CancellationStepIdentifier.SAVING_TIME_AND_MONEY,
      additionalDetails: {
        hasPersonalizedSavingsPage: data.showPersonalizedPage,
        financialAccountConnectionAvailable: data.faAccountAlertType,
        discountAvailable: eligibleForDiscount,
      },
    })(dispatch)
    navigate('/user-cancellation', { state: { recordId: record?.id } })
  }, [dispatch, navigate, data, eligibleForDiscount])

  useEffect(() => {
    pageView('plans and billing page')
  }, [pageView])

  const redirectToStripePortal = useCallback(async () => {
    track('manage stripe plan')

    const res = await fetchBillingPortalSession()(dispatch)
    if (res) {
      window.location.href = res.url
    }
  }, [dispatch, track])

  const downloadZipFile = async () => {
    track('download account zip file')

    const result = await downloadAccountInformation(currentUser?.id)(dispatch)
    if (result) {
      setDownloadModalOpen(true)
    }
  }

  useEffect(() => {
    dispatch(fetchSubscriptions())
    dispatch(fetchPayrollProfileIfNeeded())
    dispatch(fetchUserTransactions())
    dispatch(fetchUserTaxEstimates())
    dispatch(fetchCurrentUserDetailsIfNeeded(true))
    dispatch(fetchAllAnnualTaxDetailsIfNeeded())
    dispatch(fetchAnnualTaxFilingsIfNeeded())
    dispatch(fetchAnnualTaxFilingFormsIfNeeded())
  }, [dispatch])

  const renderPlanButtons = () => {
    if (isInActiveFreeTrial) {
      return (
        <Grid.Row id="reactivation">
          <Grid.Column width={4}>
            <Button
              variant="primary"
              fullWidth
              onClick={() => {
                track('click purchase plan')
                navigate('/accounts/choose-plan')
              }}
            >
              Purchase a Heard Plan
            </Button>
          </Grid.Column>
        </Grid.Row>
      )
    } else {
      return (
        <>
          {membershipIsCancelling && (
            <Grid.Row id="reactivation">
              <Grid.Column width={4}>
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={() => {
                    setReactivateOpen(true)
                    track('click reactivate plan')
                  }}
                >
                  Reactivate Plan
                </Button>
                {reactivateOpen && (
                  <ReactivatePlanModal
                    open={reactivateOpen}
                    close={() => setReactivateOpen(false)}
                  />
                )}
              </Grid.Column>
              <Grid.Column width={4}>
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={downloadZipFile}
                  loading={downloadingFiles}
                  disabled={downloadingFiles}
                >
                  Download Account Information
                </Button>
                <AccountDownloadModal
                  open={downloadModalOpen}
                  close={() => setDownloadModalOpen(false)}
                />
              </Grid.Column>
            </Grid.Row>
          )}
          {!membershipIsCancelling && (
            <Grid.Row>
              <Grid.Column width={4}>
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={redirectToStripePortal}
                >
                  Change Plan
                </Button>
                <br />

                <Grid.Column width={4}>
                  {!membershipHasManualRequest && (
                    <Button
                      variant="actionLink"
                      fullWidth
                      onClick={() => {
                        if (canSelfServeCancel) {
                          saveFlowCancellationAction()
                        } else {
                          setCancelPlanModalOpen(true)
                        }
                        sendUserCancellationMessageZap(
                          currentUser,
                          'clicked_canceled'
                        )
                        track('click cancel plan')
                      }}
                      disabled={isLoadingTransactionsAndUserTaxEstimates}
                    >
                      Cancel Plan
                    </Button>
                  )}

                  {cancelPlanModalOpen && (
                    <ConfirmManualCancellationModal
                      open={cancelPlanModalOpen}
                      close={() => setCancelPlanModalOpen(false)}
                    />
                  )}
                </Grid.Column>
              </Grid.Column>
            </Grid.Row>
          )}

          <InvoiceList />
        </>
      )
    }
  }

  return (
    <Card>
      <Grid>
        <GridRowColumn>
          <ResponsiveHeader content="Plan & Billing" />
        </GridRowColumn>
        <GridRowColumn>
          <Table basic="very">
            {!isMobile && (
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Plan</Table.HeaderCell>
                  <Table.HeaderCell>Price</Table.HeaderCell>
                  <Table.HeaderCell>Billing Start Date</Table.HeaderCell>
                  <Table.HeaderCell>Next Billing Date</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
            )}
            {isInActiveFreeTrial && (
              <Table.Body>
                <Table.Row>
                  <Table.Cell colSpan="4">
                    <Alert>
                      <Text as="h3">
                        {' '}
                        You are previewing the Heard Experience, with{' '}
                        {moment(trialEndsAt).diff(moment(), 'days')} days
                        remaining in your trial
                      </Text>
                    </Alert>
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            )}
            {!isInActiveFreeTrial && (
              <Table.Body>
                {Object.values(subscription).map((sub) => (
                  <Table.Row key={sub.id}>
                    <Table.Cell>{sub.name}</Table.Cell>
                    <Table.Cell>
                      {Object.values(sub.items).map((item) => (
                        <p key={item.id}>
                          {item.name !== SUBSCRIPTION_DEFAULT_KEY && (
                            <>
                              {item.name}
                              {item.quantity > 1 && (
                                <>&nbsp;({item.quantity} total)</>
                              )}
                              :&nbsp;
                            </>
                          )}
                          <Text>
                            <CurrencyFormatLabel
                              value={
                                centsToCurrency(item.amountInCents).multiply(
                                  item.quantity
                                ).value
                              }
                            />
                          </Text>
                        </p>
                      ))}
                    </Table.Cell>
                    {isMobile && <Table.Cell />}
                    <Table.Cell>
                      {isMobile && (
                        <Text as="bodyXs"> BILLING START DATE </Text>
                      )}
                      {moment
                        .unix(sub.startDate)
                        .format(DATE_FORMATS.DISPLAY_LONG)}
                    </Table.Cell>
                    <Table.Cell>
                      {isMobile && <Text as="bodyXs"> NEXT BILLING DATE </Text>}
                      {moment
                        .unix(sub.nextBillingDate)
                        .format(DATE_FORMATS.DISPLAY_LONG)}
                    </Table.Cell>
                  </Table.Row>
                ))}
                {membershipIsCancelling && (
                  <Table.Row>
                    <Table.Cell>
                      <Label color="red" icon={regular('triangle-exclamation')}>
                        Subscription Ending on:{' '}
                        {primarySubscription?.nextBillingDate &&
                          moment
                            .unix(primarySubscription.nextBillingDate)
                            .format(DATE_FORMATS.DISPLAY_LONG)}
                      </Label>
                    </Table.Cell>
                  </Table.Row>
                )}
                {membershipHasManualRequest && (
                  <Table.Row>
                    <Table.Cell>
                      <Label color="red" icon={regular('triangle-exclamation')}>
                        You&#39;ve requested the cancellation of your Heard
                        plan. We&#39;ll get back to you as soon as we can.
                      </Label>
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            )}
          </Table>
        </GridRowColumn>
        {renderPlanButtons()}
        <Grid.Row />
      </Grid>
    </Card>
  )
}

export default PlansAndBillingPanel
